import { useState, useEffect } from "react";
import { Loader2, Plus, Trash2, MessageSquare } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

function fullTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export function BookingNotesPanel({ booking, onChanged }) {
  const [notes, setNotes] = useState(booking?.admin_notes || []);
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Re-sync when parent booking prop refreshes
  useEffect(() => {
    setNotes(booking?.admin_notes || []);
  }, [booking?.admin_notes, booking?.id]);

  const add = async () => {
    const t = newNote.trim();
    if (!t) return;
    if (t.length > 2000) { toast.error("Note too long (max 2000 chars)"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/bookings/${booking.id}/notes`, { note: t }, { headers: auth() });
      setNotes([...notes, r.data.note]);
      setNewNote("");
      toast.success("Note added");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add note");
    }
    setSubmitting(false);
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    setDeletingId(noteId);
    try {
      await axios.delete(`${API}/admin/bookings/${booking.id}/notes/${noteId}`, { headers: auth() });
      setNotes(notes.filter((n) => n.id !== noteId));
      toast.success("Note deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
    setDeletingId(null);
  };

  return (
    <div data-testid="notes-panel">
      <div className="mb-4">
        <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1.5">Add internal note (admin-only)</label>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Notes are never visible to customers."
          className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] focus:border-[#9A7B4F] focus:outline-none resize-none"
          data-testid="new-note-input"
        />
        <div className="flex items-center justify-between mt-1.5">
          <div className="text-[10px] text-[#676664]/40">{newNote.length}/2000</div>
          <Button onClick={add} disabled={submitting || !newNote.trim()} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4" data-testid="add-note-btn">
            {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            Add note
          </Button>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="text-center py-8 text-[12px] text-[#676664]/50 italic">
          <MessageSquare className="w-5 h-5 mx-auto mb-1 opacity-40" />
          No internal notes yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="notes-list">
          {notes.slice().reverse().map((n) => (
            <div key={n.id} className="bg-amber-50/40 border border-amber-100 p-3 flex justify-between items-start gap-2" data-testid={`note-${n.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-[#282828] whitespace-pre-wrap break-words">{n.note}</div>
                <div className="text-[10px] text-[#676664]/60 mt-1">by {n.admin_email || "admin"} · {fullTime(n.created_at)}</div>
              </div>
              <button onClick={() => remove(n.id)} disabled={deletingId === n.id} className="text-[#676664]/40 hover:text-red-500 p-1" data-testid={`delete-note-${n.id}`}>
                {deletingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
